import { parse } from "node-html-parser";

/**
 * Google Docs / rich-text imports often use <span style="font-weight:700"> for bold.
 * We strip dangerous inline CSS but keep safe typography so bold/italic survive.
 */
function sanitizeInlineStyle(styleStr) {
  if (!styleStr || typeof styleStr !== "string") return null;
  const out = [];
  for (const chunk of styleStr.split(";")) {
    const idx = chunk.indexOf(":");
    if (idx === -1) continue;
    const prop = chunk.slice(0, idx).trim().toLowerCase();
    const val = chunk.slice(idx + 1).trim();
    if (!/^[a-z-]+$/.test(prop)) continue;
    const vNorm = val.replace(/\s+/g, "").toLowerCase();
    if (prop === "font-weight") {
      if (/^(bold|bolder|normal|lighter|[1-9]00)$/.test(vNorm)) {
        out.push(`font-weight: ${val}`);
      }
    } else if (prop === "font-style") {
      const v = val.trim().toLowerCase();
      if (/^(italic|normal|oblique)$/.test(v)) {
        out.push(`font-style: ${val}`);
      }
    }
  }
  return out.length ? out.join("; ") : null;
}

/** Detects common HTML answers from CMS (e.g. wrapped in <p>). */
export function looksLikeFaqHtml(s) {
  const t = String(s || "").trim();
  return t.length >= 3 && /<[a-z][\w:-]*/i.test(t);
}

/** Plain text for JSON-LD / search; avoids treating "a < b" as HTML. */
export function faqAnswerToPlainText(answer) {
  const raw = String(answer || "");
  if (!looksLikeFaqHtml(raw)) return raw.replace(/\s+/g, " ").trim();
  return parse(raw).text.replace(/\s+/g, " ").trim();
}

/**
 * Remove dangerous tags/attributes; keep basic formatting (p, br, lists, links, strong…).
 */
export function sanitizeFaqAnswerHtml(html) {
  const root = parse(String(html || ""), { lowerCaseTagName: true });
  root
    .querySelectorAll(
      "script, style, iframe, object, embed, form, input, button, link, meta, base, frame"
    )
    .forEach((el) => el.remove());

  root.querySelectorAll("*").forEach((el) => {
    const tag = String(el.rawTagName || el.tagName || "").toLowerCase();
    const attrs = el.attributes || {};
    Object.keys(attrs).forEach((name) => {
      const lower = String(name).toLowerCase();
      const val = String(attrs[name] ?? "");
      if (lower.startsWith("on")) {
        el.removeAttribute(name);
        return;
      }
      if (lower === "style") {
        const safe = sanitizeInlineStyle(val);
        el.removeAttribute("style");
        if (safe) {
          el.setAttribute("style", safe);
        }
      }
      if (tag === "a" && lower === "href" && /^\s*javascript:/i.test(val)) {
        el.removeAttribute("href");
      }
    });

    if (tag === "a") {
      const href = el.getAttribute("href");
      el.removeAttribute("href");
      if (href && /^https?:\/\//i.test(href)) {
        el.setAttribute("href", href);
        el.setAttribute("rel", "noopener noreferrer");
        el.setAttribute("target", "_blank");
      }
    }
  });

  return root.toString();
}

/** One string per <p>; if none, whole document text as a single block. */
export function extractPlainParagraphsFromFaqHtml(html) {
  const root = parse(String(html || ""), { lowerCaseTagName: true });
  const ps = root.querySelectorAll("p");
  if (ps.length) {
    return ps.map((p) => p.text.replace(/\s+/g, " ").trim()).filter(Boolean);
  }
  const text = root.text.replace(/\s+/g, " ").trim();
  return text ? [text] : [];
}
